import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box24Regular, Save20Regular, Send20Regular } from "@fluentui/react-icons";
import { Spinner } from "@fluentui/react-components";
import { FileReference } from "../OiReport";
import RawMaterialMilling from "./RawMaterialMilling";
import RawMaterialOrder from "./RawMaterialOrder";
import RawMaterialSupplierInfo from "./RawMaterialSupplierInfo";
import OiPowderTags from "../PowderOrder/OiPowderTags";
import OiPowderPressing from "../PowderOrder/OiPowderPressing";
import OiPowderSintering from "../PowderOrder/OiPowderSintering";
import OiPowderComment from "../PowderOrder/OiPowderComment";
import OiPageHeader, { OiHeaderButton } from "../OiPageHeader";
import { useRawMaterialControl } from "../redux/ReduxContext";
import OiPowderStaff from "../PowderOrder/OiPowderStaff";
import OiPowderFiles from "../PowderOrder/OiPowderFiles";
import { email } from "../../../App";
import { CreateRawMaterialControl, GetRawMaterialControl, UpdateRawMaterialControl } from "../../../services/RawMaterialService";
import { RawMaterialControl } from "../redux/rawMaterialControl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { fadeIn } from "../EventReport/OiForm";
import PowderInfo from "../PowderOrder/PowderInfo";
import PowderRawMaterialComposition from "../PowderOrder/PowderRawMaterialComposition";


interface OiPowderFormProps {
    onOpenFileGallery?: (files: FileReference[], initialIndex: number) => void;
}

export default function OiPowderForm({ onOpenFileGallery }: OiPowderFormProps) {
    const hasLoaded = useRef(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const { state, dispatch } = useRawMaterialControl();
    const [orderId, setOrderId] = useState<string>("");
    const [createdAt, setCreatedAt] = useState<string>("");
    const [updatedAt, setUpdatedAt] = useState<string>("");
    const navigate = useNavigate();
    const { uuid } = useParams<{ uuid: string }>();
    const location = useLocation();
    const { key } = location;
    const [mode, setMode] = useState<"Create" | "Edit" | "Clone">("Create");
    const [isInitializing, setIsInitializing] = useState(true);

    const hydrateForm = useCallback(async (nextUuid: string, cancelled = false) => {
        try {
            const report = await GetRawMaterialControl(nextUuid);

            if (!report || cancelled) {
                return;
            }

            const data: RawMaterialControl = JSON.parse(report.jsonData);
            setOrderId(report?.orderId || "some error");
            setCreatedAt(report?.createdAt || "");
            setUpdatedAt(report?.lastUpdated || "");
            console.log("Hydrating raw material control with data:", report?.orderId);
            dispatch({ type: 'HYDRATE', payload: data });
            dispatch({ type: 'SET_FIELD', payload: { field: 'orderId', value: report?.orderId } });
        } catch (error) {
            console.error("Error fetching raw material control:", error);
        } finally {
            if (!cancelled) {
                setIsInitializing(false);
            }
        }
    }, [dispatch]);

    useLayoutEffect(() => {
        let cancelled = false;

        const initializeForm = async () => {
            setIsInitializing(true);

            if (location.pathname === `/Oi/rawMaterialControlEdit/${uuid}`) {
                setMode("Edit");
                setOrderId("");

                if (uuid) {
                    await hydrateForm(uuid, cancelled);
                    return;
                }
            }
            else if (location.pathname === `/Oi/rawMaterialControlClone/${uuid}`) {
                setMode("Clone");
                setOrderId("");

                if (uuid) {
                    await hydrateForm(uuid, cancelled);
                    return;
                }
            }
            else if (location.pathname === `/Oi/rawMaterialControl/new`) {
                setMode("Create");
                setOrderId("");
                dispatch({
                    type: 'CLEAR_STATE',
                    payload: { author: email || "" }
                });
            }

            if (!cancelled) {
                setIsInitializing(false);
            }
        };

        initializeForm();

        return () => {
            cancelled = true;
        };
    }, [dispatch, hydrateForm, key, location.pathname, uuid]);


    const autoSave = useCallback(async () => {
        if (mode === "Edit") {
            const payload = JSON.stringify(state);
            if (uuid) {
                await UpdateRawMaterialControl(uuid, payload, true);
            }
        }
        else if (mode === "Create" || mode === "Clone") {
            const stateToSave = { ...state };
            stateToSave.author = email;
            const payload = JSON.stringify(stateToSave);

            const response = await CreateRawMaterialControl(payload, true, email);
            const parsed = JSON.parse(response || "{}");
            if (parsed.uuid) {
                navigate(`/Oi/rawMaterialControlEdit/${parsed.uuid}`);
            }
        }
    }, [mode, navigate, state, uuid]);




    useEffect(() => {
        if (!hasLoaded.current) {
            hasLoaded.current = true;
            return;
        }

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {

            autoSave();
            console.log("Auto-saving raw material control...", state);

        }, 1000);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };

    }, [autoSave, state]);

    const save = async (isDraft: boolean) => {
        if (mode === "Create" || mode === "Clone") {
            let payload = JSON.stringify(state);
            const response = await CreateRawMaterialControl(payload, isDraft, email);
            const parsed = JSON.parse(response || "{}");
            navigate(`/Oi/rawMaterialControl/${parsed.uuid}`);
        }
        else if (mode === "Edit") {
            const payload = JSON.stringify(state);
            if (uuid) {
                await UpdateRawMaterialControl(uuid, payload, isDraft);
                navigate(`/Oi/rawMaterialControl/${uuid}`);
            }
        }
    }

    if (isInitializing) {
        return (
            <div className="h-screen flex items-center justify-center w-full max-w-none mx-6 pb-4" style={{ animation: "fadeIn 1s forwards" }}>
                <style>{fadeIn}</style>
                <Spinner />
            </div>
        );
    }

    return (
        <div className="w-full max-w-none mx-6 pb-4 text-primary-dark dark:text-primary-light" style={{ animation: "fadeIn 1s forwards" }}>
            <style>{fadeIn}</style>
            <OiPageHeader
                icon={<Box24Regular />}
                title={mode === "Edit" ? "Edit raw material control" : mode === "Clone" ? "Clone raw material control" : "New raw material control"}
                id={mode === "Create" ? undefined : orderId}
                createdAt={mode === "Edit" ? createdAt : undefined}
                updatedAt={mode === "Edit" ? updatedAt : undefined}
                actions={<>
                    {mode === "Edit" && <OiHeaderButton variant="secondary" onClick={() => navigate(`/Oi/rawMaterialControl/${uuid}`)}>Back</OiHeaderButton>}
                    <OiHeaderButton variant="secondary" icon={<Save20Regular />} onClick={() => save(true)}>Save draft</OiHeaderButton>
                    <OiHeaderButton icon={<Send20Regular />} onClick={() => save(false)}>Submit</OiHeaderButton>
                </>}
            />

            <div className="grid w-full xl:gap-4 md:grid-cols-1 xl:grid-cols-2">
                <PowderInfo />
                <OiPowderComment />

            </div>
            <RawMaterialSupplierInfo />
            <RawMaterialMilling />
            <RawMaterialOrder />
            <div className="grid w-full grid-cols-1 gap-4 2xl:grid-cols-2 2xl:items-start">
                <div>
                    <OiPowderPressing />
                    <OiPowderSintering />

                </div>

                <div>
                    <OiPowderTags />
                    <OiPowderStaff />
                    <OiPowderFiles onOpenFileGallery={onOpenFileGallery} />

                </div>

            </div>


        </div>
    );
};
